import path from 'path';
import { FileSystemAdapter, realFileSystemAdapter } from './utils/file-system-adapter.js';
import { loadLlmailConfig, ResolvedConfig } from './utils/config.js';
import { IssueService } from './services/issue-service.js';
import { PathnameService } from './services/pathname-service.js';
import { IntentService } from './services/intent-service.js';
import { FileService } from './services/file-service.js';
import { FileFormatValidator } from './validation/file-format-validator.js';
import { MetadataFormatValidator } from './validation/metadata-format-validator.js';
import { FileSystemValidator } from './validation/file-system-validator.js';
import { PluginService, Plugin } from './services/plugin-service.js';
import { generateId } from './utils/id-generator.js';
import { FrontmatterMetadata, OperationResult } from './types.js';
import { parseDocSections, rebuildDocSections, ensureTimestamps } from './utils/doc-sections';
import { loadPlugins } from './utils/plugin-loader.js';
import { logger } from './utils/logger.js';

export interface LLMailOptions {
  fs?: FileSystemAdapter;
  config?: ResolvedConfig;
  plugins?: Plugin[];
  rootDir?: string;
  pluginService?: PluginService;
}

export interface MoveOptions {
  state?: string;
  type?: string;
  active?: boolean;
  inactive?: boolean;
}

export interface NewOptions {
  title?: string;
  content?: string;
  state?: string;
  frontmatter?: Record<string, any>;
}

export interface DoneOptions {
  state?: string;
  frontmatter?: Record<string, any>;
}

export interface SyncOptions {
  include?: string;
  exclude?: string;
  dryRun?: boolean;    // Just show what would change
  force?: boolean;     // Skip confirmations
}

export interface SyncResult {
  changes: Array<{
    id: string;
    from: string;
    to: string;
    state: string;
  }>;
  errors?: Array<{
    id: string;
    error: string;
  }>;
}

export interface Issue {
  id: string;
  filePath: string;
  metadata: FrontmatterMetadata;
  content?: string;
}

/**
 * Main class for managing issues and their lifecycle.
 * Provides a high-level API that coordinates between various services.
 */
export class LLMail {
  private fs: FileSystemAdapter;
  private config?: ResolvedConfig;
  private issueService!: IssueService;
  private pathnameService!: PathnameService;
  private intentService!: IntentService;
  private fileService!: FileService;
  private pluginService!: PluginService;
  private plugins: Plugin[];
  private rootDir: string;

  constructor(options: LLMailOptions = {}) {
    this.fs = options.fs || realFileSystemAdapter;
    this.config = options.config;
    this.plugins = options.plugins || [];
    this.rootDir = options.rootDir || process.cwd();
    if (options.pluginService) {
      this.pluginService = options.pluginService;
    }
  }

  /**
   * Initialize the LLMail system and all its services
   */
  async init(): Promise<void> {
    // Load config if not already provided
    if (!this.config) {
      this.config = await loadLlmailConfig(this.fs, this.rootDir);
    }

    logger.debug('LLMail init - Config loaded:', this.config);

    // Load plugins from config if not provided in constructor
    if (this.plugins.length === 0 && this.config.plugins) {
      logger.debug('LLMail init - Loading plugins from config:', this.config.plugins);
      try {
        this.plugins = await loadPlugins(this.config);
        logger.debug('LLMail init - Plugins loaded:', this.plugins.map(p => p.name));
      } catch (error) {
        logger.error('LLMail init - Error loading plugins:', error);
        throw error;  // Re-throw the error to prevent continuing with invalid plugins
      }
    }

    // Initialize plugin service if not provided
    if (!this.pluginService) {
      logger.debug('LLMail init - Initializing plugin service');
      this.pluginService = new PluginService();
      
      // Register core types BEFORE registering plugins
      logger.debug('LLMail init - Registering core types:', this.config.types_list);
      this.pluginService.registerCoreTypes(this.config.types_list);
      this.pluginService.registerCoreActiveStates(this.config.active_states);
      this.pluginService.registerCoreInactiveReasons(this.config.inactive_reasons);
      
      // Now register plugins
      logger.debug('LLMail init - Registering plugins:', this.plugins.map(p => p.name));
      for (const plugin of this.plugins) {
        try {
          logger.debug(`LLMail init - Registering plugin ${plugin.name}`);
          await this.pluginService.registerPlugin(plugin);
          logger.debug(`LLMail init - Plugin ${plugin.name} registered successfully`);
        } catch (error) {
          logger.error(`LLMail init - Error registering plugin ${plugin.name}:`, error);
          throw error;  // Re-throw the error to prevent continuing with invalid plugins
        }
      }
    }

    // Initialize core services with plugin service
    this.intentService = new IntentService(this.config, this.pluginService);
    this.pathnameService = new PathnameService(this.config, this.intentService);
    this.fileService = new FileService(this.fs, this.pathnameService);

    // Initialize validators
    const fileFormatValidator = new FileFormatValidator();
    const metadataValidator = new MetadataFormatValidator();
    const fileSystemValidator = new FileSystemValidator(this.fs);

    // Initialize issue service with all dependencies
    this.issueService = new IssueService(
      this.pathnameService,
      this.fs,
      this.intentService,
      this.fileService,
      fileFormatValidator,
      metadataValidator,
      fileSystemValidator,
      this.pluginService
    );

    // Create required directories
    const rootDir = this.config.issues_dir.replace(/^\/+/, '');
    const inboxDir = this.config.inbox_dir.replace(/^\/+/, '');

    await this.fs.ensureDir(path.join(this.rootDir, rootDir));
    await this.fs.ensureDir(path.join(this.rootDir, inboxDir));

    // Create type directories
    for (const type of this.config.types_list) {
      await this.fs.ensureDir(path.join(this.rootDir, rootDir, type));
    }

    // Create state directories
    for (const state of this.config.active_states) {
      await this.fs.ensureDir(path.join(this.rootDir, rootDir, `_${state}`));
    }
  }

  private ensureInitialized(): void {
    if (!this.issueService || !this.pathnameService || !this.intentService || !this.fileService) {
      throw new Error('LLMail not initialized. Call init() first.');
    }
  }

  /**
   * Create a new issue
   */
  async new(type: string | undefined, options: NewOptions = {}): Promise<string> {
    this.ensureInitialized();
    
    // If no type is provided, use the default type from intent service
    const metadata: FrontmatterMetadata = {
      id: generateId(),
      type: type || 'issue',  // Default to 'issue' if no type provided
      active: true,  // new items start active
      created: new Date().toISOString()
    };

    // Let intent service interpret and validate the metadata
    const interpretedMetadata = this.intentService.interpretMetadata(metadata);

    // Merge frontmatter if provided
    if (options.frontmatter) {
      Object.assign(interpretedMetadata, options.frontmatter);
      // Re-assert critical fields that shouldn't be overwritten
      interpretedMetadata.type = interpretedMetadata.type;  // Keep interpreted type
      interpretedMetadata.id = interpretedMetadata.id;  // Keep original generated ID
      interpretedMetadata.active = true;
      interpretedMetadata.created = interpretedMetadata.created;  // Keep original timestamp
    }

    if (options.title) {
      interpretedMetadata.title = options.title;
    }

    if (options.state) {
      // Use IntentService to validate the state
      if (this.intentService.isValidState(options.state)) {
        interpretedMetadata.state = options.state;
        interpretedMetadata.active = this.intentService.isActiveState(options.state);
      } else {
        throw new Error(`Invalid state: ${options.state}`);
      }
    }

    // Get the relative path from IssueService
    const relativePath = await this.issueService.fileMethods.writeNewFile(interpretedMetadata, options.content || '');
    
    // Return just the ID since that's what the API expects
    return interpretedMetadata.id;
  }

  /**
   * Move an issue to a new state or type
   */
  async mv(id: string, options: MoveOptions = {}): Promise<OperationResult> {
    logger.debug('LLMail.mv - Starting move operation');
    logger.debug('LLMail.mv - Options:', options);

    // Get the issue first to get its file path
    const issue = await this.getIssueById(id);
    if (!issue) {
      throw new Error(`Issue ${id} not found`);
    }

    logger.debug('LLMail.mv - Initial metadata:', issue.metadata);
    logger.debug('LLMail.mv - Initial filepath:', issue.filePath);

    // Start with current metadata
    const updates: Partial<FrontmatterMetadata> = {};

    // Handle type change first if specified
    if (options.type) {
      logger.debug('LLMail.mv - Processing type change');
      if (!this.intentService.getValidTypes().includes(options.type)) {
        throw new Error(`Invalid type: ${options.type}`);
      }
      updates.type = options.type;
    }

    // Then handle state change if specified
    if (options.state) {
      logger.debug('LLMail.mv - Processing state change');
      if (!this.intentService.isValidState(options.state)) {
        throw new Error(`Invalid state: ${options.state}`);
      }
      updates.state = options.state;
    }

    // Handle active/inactive flags
    if (options.active !== undefined) {
      logger.debug('LLMail.mv - Setting active flag:', options.active);
      updates.active = options.active;
      // Clear state when setting active without a specific state
      if (options.active && !options.state) {
        logger.debug('LLMail.mv - Clearing state due to active flag');
        updates.state = undefined;
      }
    }
    if (options.inactive !== undefined) {
      logger.debug('LLMail.mv - Setting inactive flag:', !options.inactive);
      updates.active = !options.inactive;
    }

    logger.debug('LLMail.mv - Final updates:', updates);

    // Apply the updates
    logger.debug('LLMail.mv - Applying frontmatter updates');
    await this.updateFrontmatter(id, updates);

    // Sync files to ensure they are in the correct locations
    logger.debug('LLMail.mv - Starting sync operation');
    const syncResult = await this.sync({ force: true });
    logger.debug('LLMail.mv - Sync completed with result:', syncResult);

    // Return success
    const newIssue = await this.getIssueById(id);
    logger.debug('LLMail.mv - Final issue state:', newIssue);
    return {
      success: true,
      filepath: newIssue!.filePath
    };
  }

  // Add move as an alias for mv for backward compatibility
  async move(id: string, options: MoveOptions): Promise<OperationResult> {
    return this.mv(id, options);
  }

  /**
   * Get an issue by ID
   */
  async getIssueById(id: string): Promise<Issue | undefined> {
    this.ensureInitialized();
    
    const filePath = await this.issueService.fileMethods.findFileById(id);
    if (!filePath) return undefined;

    const metadata = await this.issueService.frontmatterMethods.getFrontmatterMetadata(filePath);
    if (!metadata) return undefined;

    const content = await this.fileService.readFile(filePath);
    
    return { id, filePath, metadata, content };
  }

  /**
   * Update issue frontmatter
   */
  async updateFrontmatter(id: string, updates: Partial<FrontmatterMetadata>): Promise<void> {
    this.ensureInitialized();
    
    const issue = await this.getIssueById(id);
    if (!issue) {
      throw new Error(`Issue ${id} not found`);
    }

    await this.issueService.frontmatterMethods.setFrontmatterMetadata(
      issue.filePath,
      { ...issue.metadata, ...updates }
    );
  }

  /**
   * Append an update to an issue
   */
  async appendUpdate(id: string, content: string, metadata?: Record<string, string>, time: boolean = true): Promise<void> {
    this.ensureInitialized();
    
    await this.issueService.xmlMethods.appendUpdate({
      id,
      content,
      metadata,
      time
    });
  }

  /**
   * Prepend an update to an issue
   */
  async prependUpdate(id: string, content: string, metadata?: Record<string, string>, time: boolean = true): Promise<void> {
    this.ensureInitialized();
    
    await this.issueService.xmlMethods.prependUpdate({
      id,
      content,
      metadata,
      time
    });
  }

  /**
   * Sync file locations with their metadata
   */
  async sync(options: SyncOptions = {}): Promise<SyncResult> {
    this.ensureInitialized();
    logger.debug('LLMail.sync - Starting sync operation with options:', options);

    // Get all files by finding files in each state
    const allFiles = new Set<string>();
    
    // Check state directories
    const allStates = [...this.config!.active_states, ...this.config!.inactive_reasons];
    logger.debug('LLMail.sync - Checking state directories:', allStates);
    for (const state of allStates) {
      const files = await this.issueService.fileMethods.findFilesByState(state);
      logger.debug(`LLMail.sync - Found files for state ${state}:`, files);
      files.forEach(f => allFiles.add(f));
    }

    // Check inbox
    logger.debug('LLMail.sync - Checking inbox directory');
    const inboxFiles = await this.issueService.fileMethods.findFilesByState('inbox');
    logger.debug('LLMail.sync - Found files in inbox:', inboxFiles);
    inboxFiles.forEach(f => allFiles.add(f));

    // Check type directories
    logger.debug('LLMail.sync - Checking type directories:', this.config!.types_list);
    for (const type of this.config!.types_list) {
      const files = await this.issueService.fileMethods.findFilesByType(type);
      logger.debug(`LLMail.sync - Found files for type ${type}:`, files);
      files.forEach(f => allFiles.add(f));
    }

    // Check root directory
    logger.debug('LLMail.sync - Checking root directory');
    const rootFiles = await this.fileService.findFiles(
      (f) => f.endsWith('.md'),
      [this.config!.issues_dir]
    );
    logger.debug('LLMail.sync - Found files in root:', rootFiles);
    rootFiles.forEach(f => allFiles.add(f));

    if (allFiles.size === 0) {
      logger.debug('LLMail.sync - No files found to sync');
      return { changes: [] };
    }

    logger.debug('LLMail.sync - Total files to process:', allFiles.size);
    const changes: Array<{ id: string; from: string; to: string; state: string }> = [];
    const errors: Array<{ id: string; error: string }> = [];

    for (const filePath of allFiles) {
      try {
        logger.debug(`LLMail.sync - Processing file: ${filePath}`);
        const metadata = await this.issueService.frontmatterMethods.getFrontmatterMetadata(filePath);
        if (!metadata) {
          logger.debug(`LLMail.sync - No metadata found for file: ${filePath}`);
          continue;
        }

        const targetLocation = this.pathnameService.deriveFileLocation(metadata);
        const targetPath = path.join(targetLocation.dirname, targetLocation.basename);
        logger.debug(`LLMail.sync - Target path for file: ${targetPath}`);

        if (targetPath !== filePath) {
          logger.debug(`LLMail.sync - File needs to be moved from ${filePath} to ${targetPath}`);
          if (!options.dryRun) {
            await this.issueService.fileMethods.moveFileWithMetadata(filePath, targetPath, metadata);
            logger.debug('LLMail.sync - File moved successfully');
          }
          changes.push({
            id: metadata.id,
            from: filePath,
            to: targetPath,
            state: metadata.state ?? ''
          });
        } else {
          logger.debug('LLMail.sync - File is already in correct location');
        }
      } catch (err) {
        const id = path.basename(filePath).split('-')[1]?.split('.')[0] || path.basename(filePath);
        logger.error(`LLMail.sync - Error processing file ${filePath}:`, err);
        errors.push({
          id,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }

    logger.debug('LLMail.sync - Sync completed with changes:', changes);
    if (errors.length > 0) {
      logger.debug('LLMail.sync - Sync completed with errors:', errors);
    }

    return {
      changes,
      errors: errors.length > 0 ? errors : undefined
    };
  }

  /**
   * Get all valid issue types
   */
  async getIssueTypes(): Promise<string[]> {
    this.ensureInitialized();
    return this.config!.types_list;
  }

  /**
   * Get all valid issue states
   */
  async getIssueStates(): Promise<string[]> {
    this.ensureInitialized();
    return [...this.config!.active_states, ...this.config!.inactive_reasons];
  }

  /**
   * Find issues by type
   */
  async findIssuesByType(type: string): Promise<Issue[]> {
    this.ensureInitialized();
    
    if (!this.config!.types_list.includes(type)) {
      throw new Error(`Invalid type: ${type}`);
    }

    const files = await this.issueService.fileMethods.findFilesByType(type);
    const issues: Issue[] = [];

    for (const filePath of files) {
      const metadata = await this.issueService.frontmatterMethods.getFrontmatterMetadata(filePath);
      if (metadata) {
        const content = await this.fileService.readFile(filePath);
        issues.push({ id: metadata.id, filePath, metadata, content });
      }
    }

    return issues;
  }

  /**
   * Find issues by state
   */
  async findIssuesByState(state: string): Promise<Issue[]> {
    this.ensureInitialized();
    
    if (!this.config!.active_states.includes(state) && !this.config!.inactive_reasons.includes(state)) {
      throw new Error(`Invalid state: ${state}`);
    }

    const files = await this.issueService.fileMethods.findFilesByState(state);
    const issues: Issue[] = [];

    for (const filePath of files) {
      const metadata = await this.issueService.frontmatterMethods.getFrontmatterMetadata(filePath);
      if (metadata) {
        const content = await this.fileService.readFile(filePath);
        issues.push({ id: metadata.id, filePath, metadata, content });
      }
    }

    return issues;
  }

  async done(id: string, options: DoneOptions = {}): Promise<OperationResult> {
    this.ensureInitialized();

    const issue = await this.getIssueById(id);
    if (!issue) {
      throw new Error(`Issue ${id} not found`);
    }

    const updates: Partial<FrontmatterMetadata> = {
      active: false,
      inactive_timestamp: new Date().toISOString()
    };

    // If state is provided, validate and use it
    if (options.state) {
      if (!this.config!.inactive_reasons.includes(options.state)) {
        throw new Error(`Invalid inactive reason: ${options.state}`);
      }
      updates.state = options.state;
    }

    // Add any additional frontmatter updates
    if (options.frontmatter) {
      Object.assign(updates, options.frontmatter);
      // Re-assert critical fields that shouldn't be overwritten
      updates.active = false;
      updates.inactive_timestamp = updates.inactive_timestamp;
    }

    // Apply updates
    await this.updateFrontmatter(id, updates);

    // Sync files to ensure they are in the correct locations
    await this.sync({ force: true });

    // Return success
    const newIssue = await this.getIssueById(id);
    return {
      success: true,
      filepath: newIssue!.filePath
    };
  }

  /**
   * Returns only the content section (excludes frontmatter and xml)
   */
  async getContent(id: string): Promise<string> {
    this.ensureInitialized();
    const filePath = await this.issueService.fileMethods.findFileById(id);
    if (!filePath) {
      throw new Error(`File not found for id: ${id}`);
    }

    const fileString = await this.fileService.readFile(filePath);
    const sections = parseDocSections(fileString);
    
    return sections.content;
  }

  /**
   * Overwrites the entire content region (between frontmatter and XML)
   */
  async setContent(id: string, newContent: string): Promise<void> {
    this.ensureInitialized();
    const filePath = await this.issueService.fileMethods.findFileById(id);
    if (!filePath) {
      throw new Error(`File not found for id: ${id}`);
    }

    const fileString = await this.fileService.readFile(filePath);
    const sections = parseDocSections(fileString);

    // Update content and timestamps
    sections.content = newContent;
    ensureTimestamps(sections.frontmatterObj);

    // Rebuild and write
    const newString = rebuildDocSections(sections);
    await this.fileService.writeFile(filePath, newString);
  }

  /**
   * Appends text to the end of content region, above the xml region
   */
  async appendContent(id: string, content: string): Promise<void> {
    this.ensureInitialized();
    const filePath = await this.issueService.fileMethods.findFileById(id);
    if (!filePath) {
      throw new Error(`File not found for id: ${id}`);
    }

    const fileString = await this.fileService.readFile(filePath);
    const sections = parseDocSections(fileString);

    // Append to content with proper spacing
    sections.content = sections.content.trimEnd() + (sections.content ? '\n\n' : '') + content;
    ensureTimestamps(sections.frontmatterObj);

    // Rebuild and write
    const newString = rebuildDocSections(sections);
    await this.fileService.writeFile(filePath, newString);
  }

  /**
   * Prepends text to the start of content region, below the frontmatter
   */
  async prependContent(id: string, content: string): Promise<void> {
    this.ensureInitialized();
    const filePath = await this.issueService.fileMethods.findFileById(id);
    if (!filePath) {
      throw new Error(`File not found for id: ${id}`);
    }

    const fileString = await this.fileService.readFile(filePath);
    const sections = parseDocSections(fileString);

    // Prepend to content with proper spacing
    sections.content = content + (sections.content ? '\n\n' : '') + sections.content.trimStart();
    ensureTimestamps(sections.frontmatterObj);

    // Rebuild and write
    const newString = rebuildDocSections(sections);
    await this.fileService.writeFile(filePath, newString);
  }

  /**
   * Provides read-access to the plugin service instance,
   * so that consumers can fetch plugin commands, etc.
   */
  getPluginService(): PluginService {
    this.ensureInitialized();
    return this.pluginService;
  }

  /**
   * Get the current resolved config
   */
  getConfig(): ResolvedConfig {
    if (!this.config) {
      throw new Error('LLMail not initialized. Call init() first.');
    }
    return this.config;
  }

  /**
   * Get the intent service instance
   */
  getIntentService(): IntentService {
    this.ensureInitialized();
    return this.intentService;
  }
} 